import { Component, Inject, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CompanyDocumentsService } from '../../service/company-documents.service';
import { CompanyDocumentDetailDTO } from '../../interface/company-document.interface';
import { Observable, catchError, map, of } from 'rxjs';
import { RequestSignatureModalComponent } from './request-signature-modal/request-signature-modal.component';
import { NgxExtendedPdfViewerService, PDFNotificationService } from 'ngx-extended-pdf-viewer';
import Swal from 'sweetalert2';
import { CompanyDocumentService, RequestSignsPayload } from '../../service/company-document.service';
import { DocumentTemplateApiCallerService } from 'src/app/pages/document-templates/services/document-template-api-caller.service';
import { SigningTypeEnum, SigningTypeViewModel, SigningTypes } from 'src/app/features/signing/models/signing.model';
import { MatSelectChange } from '@angular/material/select';
import { SignatureParticipantsService } from 'src/app/features/signature-requests/signature-participants.service';

// Configurar los assets del visor PDF
// pdfDefaultOptions.assetsFolder = '/assets/pdf-viewer';

export interface SignatureLocation {
  page: number;
  x: number;
  y: number;
  pageX: number;
  pageY: number;
  color: string;
  index: number;
  requestId: string;
}

export interface SignatureRequest {
  id: string;
  rut: string;
  nombre: string;
  email: string;
  color: string;
  isSigned: boolean;
  state?: number;
  participantId?: bigint | null | undefined;
}

export interface SignatureRequestResponse {
  rut: string;
  nombre: string;
  email: string;
  action: 'select_location' | 'cancel';
}

@Component({
  selector: 'app-preview-company-document-modal',
  templateUrl: './preview-company-document-modal.component.html',
  styleUrls: ['./preview-company-document-modal.component.scss']
})
export class PreviewCompanyDocumentModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('pdfViewer') pdfViewer: any;
  
  public signingTypes: SigningTypeViewModel[] = SigningTypes;

  public selectedSigningType: number = SigningTypeEnum.SES;
  isSelectingLocation = false;
  currentSignatureRequest: SignatureRequest | null = null;
  signatureLocations: SignatureLocation[] = [];
  currentPage = 1;
  zoom = 1;
  pdfSrc: string | Blob = '';
  base64: string = '';
  documentName: string = '';
  isLoading = false;
  viewOnly = false;
  
  // Colores predefinidos para los firmantes
  private signatureColors = [
    '#1976d2', // Azul
    '#388e3c', // Verde
    '#d32f2f', // Rojo
    '#7b1fa2', // Morado
    '#f57c00', // Naranja
    '#e91e63', // Rosa
    '#9c27b0', // Morado oscuro
    '#2196f3', // Azul claro
    '#009688', // Verde azulado
    '#ff5722', // Naranja oscuro
    '#795548', // Marrón
    '#607d8b', // Gris azulado
    '#ff9800', // Naranja claro
    '#cddc39', // Lima
    '#00bcd4'  // Cian
  ];
  private currentColorIndex = 0;

  public blobFile$: Observable<Blob | null>;
  //public companyDocument: CompanyDocumentDetailDTO | null = null;
  public companyDocument: CompanyDocumentDetailDTO = newCompanyDocumentDetail;

  // Referencia al observador de redimensionamiento
  private resizeObserver: ResizeObserver | null = null;

  signatureRequests: SignatureRequest[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PreviewCompanyDocumentModalComponent>,
    private companyDocService: CompanyDocumentsService,
    private companyDocumentService: CompanyDocumentService,
    private dialog: MatDialog,
    private documentTemplateService: DocumentTemplateApiCallerService,
    private pdfService: NgxExtendedPdfViewerService,
    private notificationService: PDFNotificationService,
    private signatureParticipantsService: SignatureParticipantsService
  ) {
    if(!data.viewOnly) {this.blobFile$ = this.companyDocService.downloadCompanyDocument(data.id);}
    if(data.viewOnly && !data.base64) {this.blobFile$ = this.documentTemplateService.downloadDocumentPreVisualizacion(data.id); this.viewOnly = true;}
    if(data.base64) {this.viewOnly = true, this.base64 = data.base64; this.isSelectingLocation = false; this.documentName = data.name; return;}
  }

  ngOnInit(): void {
    if(this.base64) {this.isSelectingLocation = false ; return;}
    if(!this.data.viewOnly) {this.getCompanyDocument(this.data.id);}
    if(this.data.viewOnly) {this.getPreVisualizacionDocument(this.data.id);}
  
    this.blobFile$.subscribe(blob => {
      if (blob) {
        this.pdfSrc = blob;
        // Obtener el nombre del documento del blob
        if (blob instanceof File) {
          this.documentName = blob.name;
        } else {
          // Si es un Blob, intentar obtener el nombre de los headers de la respuesta
          const contentDisposition = blob.type || '';
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match && match[1]) {
            this.documentName = match[1].replace(/['"]/g, '');
          }
        }
      }
    });

    // Crear un observador para manejar cambios de tamaño
    this.resizeObserver = new ResizeObserver(() => {
      this.updateSignaturePositions();
    }); 
  }

  ngAfterViewInit() {
    // Observar cambios en el tamaño del contenedor
    const viewerContainer = document.querySelector('#viewerContainer');
    if (viewerContainer && this.resizeObserver) {
      this.resizeObserver.observe(viewerContainer);
    }

    // Agregar evento para mover el rectángulo de firma
    const pdfContainer = document.querySelector('.pdf-container');
    if (pdfContainer) {
      pdfContainer.addEventListener('mousemove', (e: MouseEvent) => {
        if (this.isSelectingLocation) {
          // Encontrar la página actual bajo el cursor
          const pages = Array.from(document.querySelectorAll('.page'));
          let targetPage: Element | null = null;

          for (const page of pages) {
            const rect = page.getBoundingClientRect();
            if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
              targetPage = page;
              break;
            }
          }

          if (targetPage) {
            const pageRect = targetPage.getBoundingClientRect();
            const x = e.clientX;
            const y = e.clientY;
            
            const style = document.documentElement.style;
            style.setProperty('--cursor-x', `${x}px`);
            style.setProperty('--cursor-y', `${y}px`);
          }
        }
      });
    } 
  } 

  ngOnDestroy() {
    // Limpiar el observador
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  handlePdfClick(event: MouseEvent) {
    if (!this.isSelectingLocation || !this.currentSignatureRequest) return;

    // Obtener el contenedor del visor
    const viewerContainer = document.querySelector('#viewerContainer');
    if (!viewerContainer) return;

    // Encontrar la página que contiene el punto de click
    const pages = Array.from(document.querySelectorAll('.page'));
    let targetPage: Element | null = null;
    let pageNumber = 1;

    for (const page of pages) {
      const rect = page.getBoundingClientRect();
      if (event.clientY >= rect.top && event.clientY <= rect.bottom) {
        targetPage = page;
        pageNumber = parseInt(page.getAttribute('data-page-number') || '1', 10);
        break;
      }
    }

    if (!targetPage) return;

    // Obtener las coordenadas relativas a la página
    const pageRect = targetPage.getBoundingClientRect();
    const x = ((event.clientX - pageRect.left) / pageRect.width) * 100;
    const y = ((event.clientY - pageRect.top) / pageRect.height) * 100;
    const pageX = event.clientX - pageRect.left - 18;
    const pageY = event.clientY - pageRect.top - 9;
    

    const location: SignatureLocation = {
      page: pageNumber,
      x: x,
      y: y,
      pageX: pageX,
      pageY: pageY,
      color: this.currentSignatureRequest.color,
      index: this.getLocationsForSigner(this.currentSignatureRequest).length + 1,
      requestId: this.currentSignatureRequest.id
    };

    this.signatureLocations.push(location);
    this.addSignatureAnnotation(location);

    console.log(`Nueva ubicación de firma seleccionada:`, {
      firmante: {
        rut: this.currentSignatureRequest.rut,
        nombre: this.currentSignatureRequest.nombre,
        email: this.currentSignatureRequest.email
      },
      ubicacion: {
        pagina: location.page,
        posicionX: x,
        posicionY: y,
        numeroFirma: location.index
      }
    });
  }

  private addSignatureAnnotation(location: SignatureLocation) {
    // Obtener la página específica
    const targetPage = document.querySelector(`.page[data-page-number="${location.page}"]`);
    if (!targetPage) return;

    const signatureBox = document.createElement('div');
    signatureBox.className = 'signature-box';
    signatureBox.setAttribute('data-index', `Firma ${location.index}`);
    signatureBox.setAttribute('data-page', `${location.page}`);
    signatureBox.setAttribute('data-request-id', location.requestId);
    signatureBox.style.left = `${location.pageX}px`;
    signatureBox.style.top = `${location.pageY}px`;
    signatureBox.style.width = '200px';
    signatureBox.style.height = '100px';
    signatureBox.style.border = `2px dashed ${location.color}`;
    signatureBox.style.backgroundColor = `${location.color}1A`;
    signatureBox.style.position = 'absolute';
    signatureBox.style.zIndex = '1000';

    const nameLabel = document.createElement('div');
    nameLabel.className = 'signature-label';
    nameLabel.textContent = this.currentSignatureRequest?.nombre || '';
    signatureBox.appendChild(nameLabel);

    targetPage.appendChild(signatureBox);
  }

  private updateSignaturePositions() {
    this.signatureLocations.forEach(location => {
      const page = document.querySelector(`.page[data-page-number="${location.page}"]`);
      const signatureBox = page?.querySelector(`.signature-box[data-index="Firma ${location.index}"]`);
      if (signatureBox instanceof HTMLElement) {
        signatureBox.style.left = `${location.pageX}px`;
        signatureBox.style.top = `${location.pageY}px`;
      }
    });
  }

  onPageChange(event: any) {
    this.currentPage = event.pageNumber;
  }

  onZoomChange(event: any) {
    this.zoom = event.scale;
    this.updateSignaturePositions();
  }

  startLocationSelection(request: SignatureRequest) {
    const color = this.signatureColors[this.currentColorIndex % this.signatureColors.length];
    this.currentColorIndex++;

    this.isSelectingLocation = true;
    this.currentSignatureRequest = {
      rut: request.rut,
      nombre: request.nombre,
      email: request.email,
      color: color,
      isSigned: false,
      id: this.generateUniqueId()
    };
    this.signatureLocations = [];
  }

  finishLocationSelection() {
    if (this.currentSignatureRequest && this.signatureLocations.length > 0) {
      console.log('Ubicaciones de firma registradas:', {
        firmante: this.currentSignatureRequest,
        ubicaciones: this.getLocationsForSigner(this.currentSignatureRequest)
      });
    }
    this.isSelectingLocation = false;
    this.currentSignatureRequest = null;
  }

  cancelLocationSelection() {
    if (this.currentSignatureRequest) {
      // Eliminar las ubicaciones de firma del firmante actual
      this.signatureLocations = this.signatureLocations.filter(
        location => location.requestId !== this.currentSignatureRequest?.id
      );

      // Eliminar el firmante del resumen de firmas
      this.signatureRequests = this.signatureRequests.filter(
        request => request.id !== this.currentSignatureRequest?.id
      );

      // Eliminar las marcas visuales del PDF
      const signatureBoxes = document.querySelectorAll('.signature-box');
      signatureBoxes.forEach(box => {
        if (box instanceof HTMLElement && 
            box.getAttribute('data-request-id') === this.currentSignatureRequest?.id) {
          box.remove();
        }
      });
    }

    // Restablecer el estado
    this.isSelectingLocation = false;
    this.currentSignatureRequest = null;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  public onChangeSigningType(event: MatSelectChange) {
    this.selectedSigningType = event.value;
  }

  getCompanyDocument(companyDocumentId: bigint) {
    this.companyDocService.getCompanyDocument(companyDocumentId)
      .subscribe({
        next: (res) => {
          this.companyDocument = res;
          console.log(res);
          this.signatureRequests = res.signers.map(signer => ({
            id: "1",
            rut: signer.rut,
            nombre: signer.name,
            email: signer.email,
            color: this.signatureColors[this.currentColorIndex % this.signatureColors.length],
            isSigned: signer.signed,
            state: signer.state,
            participantId: signer.participantId
          }));
          if (res && res.uri.split('/').length > 0) {
            const encodedFileName = res.uri.split('/').pop()?.split('?')[0] || 'Previsualización de Documento';
            this.documentName = decodeURIComponent(encodedFileName)
              .replace(/\+/g, ' ') // Reemplazar + por espacio
              .replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))); // Decodificar caracteres hexadecimales
          }
        },
        error: (error) => {
          console.log('Error al obtener el documento:', error);
        }
      });
  }

  getPreVisualizacionDocument(templateId: number) {
    this.documentTemplateService.downloadDocumentPreVisualizacion(templateId)
    .subscribe({
      next: (res) => {
        this.companyDocument = res;
        if (res && res.uri.split('/').length > 0) {
          const encodedFileName = res.uri.split('/').pop()?.split('?')[0] || 'Previsualización de Documento';
          this.documentName = decodeURIComponent(encodedFileName)
            .replace(/\+/g, ' ') // Reemplazar + por espacio
            .replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))); // Decodificar caracteres hexadecimales
        }
      }
    });
  }

  openRequestSignatureModal() {
    const dialogRef = this.dialog.open(RequestSignatureModalComponent, {
      width: '500px',
      data: { companyId: this.data.id }
    });

    dialogRef.afterClosed().subscribe((result: SignatureRequestResponse) => {
      if (result && result.action === 'select_location') {
        const newRequest: SignatureRequest = {
          id: this.generateUniqueId(),
          rut: result.rut,
          nombre: result.nombre,
          email: result.email,
          color: this.getRandomColor(),
          isSigned: false,
          state: 0 // Estado inicial
        };
        
        this.signatureRequests.push(newRequest);
        this.currentSignatureRequest = newRequest;
        this.isSelectingLocation = true;
      }
    });
  }

  getLocationsForSigner(request: SignatureRequest): SignatureLocation[] {
    return this.signatureLocations.filter(location => location.requestId === request.id);
  }

  private generateUniqueId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private getRandomColor(): string {
    const colors = ['#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#c2185b'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  confirmSendToSign() {
    Swal.fire({
      title: 'Confirmar envío a firma',
      text: '¿Está seguro que desea enviar el documento a firma? Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Enviar a firma',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1976d2',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.sendToSign();
      }
    });
  }

  private sendToSign() {
    this.isLoading = true;
    const payload: RequestSignsPayload = {
      companyDocumentId: Number(this.data.id),
      signatureType: this.selectedSigningType,
      userSignRequests: this.signatureRequests.map(request => ({
        signerName: request.nombre,
        signerRut: request.rut,
        signerEmail: request.email,
        signTypeName: 'Default',
        userSignPositions: this.getLocationsForSigner(request).map(location => ({
          page: location.page,
          x: Math.round(location.pageX+5),
          y: Math.round(location.pageY+15)
        }))
      }))
    };

    this.companyDocumentService.requestSigns(payload).subscribe({
      next: (response) => {
        this.isLoading = false;
        Swal.fire({
          title: 'Éxito',
          text: 'La solicitud de firmas ha sido enviada correctamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1976d2'
        });
        this.dialogRef.close(response);
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error al enviar solicitud de firmas:', error);
        Swal.fire({
          title: 'Error',
          text: 'Ha ocurrido un error al enviar la solicitud de firmas: ' + error.message,
          icon: 'error',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1976d2'
        });
      }
    });
  }

  editSignatureLocations(request: SignatureRequest) {
    // Establecer el firmante actual
    this.currentSignatureRequest = request;
    this.isSelectingLocation = true;
  }

  deleteSignatureLocation(location: SignatureLocation) {
    // Eliminar la ubicación del array de ubicaciones usando solo el índice y requestId
    this.signatureLocations = this.signatureLocations.filter(loc => 
      !(loc.index === location.index && loc.requestId === location.requestId)
    );

    // Eliminar solo la marca visual específica
    const signatureBox = document.querySelector(
      `.signature-box[data-request-id="${location.requestId}"][data-index="Firma ${location.index}"]`
    );
    if (signatureBox) {
      signatureBox.remove();
    }

    // Obtener las ubicaciones restantes del firmante
    const signerLocations = this.signatureLocations
      .filter(loc => loc.requestId === location.requestId)
      .sort((a, b) => a.index - b.index);

    // Si no quedan ubicaciones, eliminar al firmante del resumen
    if (signerLocations.length === 0) {
      this.signatureRequests = this.signatureRequests.filter(
        request => request.id !== location.requestId
      );
      return;
    }

    // Actualizar los índices y las etiquetas de las marcas restantes
    signerLocations.forEach((loc, idx) => {
      const newIndex = idx + 1;
      const oldIndex = loc.index;
      loc.index = newIndex;

      // Actualizar solo el índice en la marca visual existente
      if (oldIndex !== newIndex) {
        const box = document.querySelector(
          `.signature-box[data-request-id="${loc.requestId}"][data-index="Firma ${oldIndex}"]`
        );
        if (box) {
          box.setAttribute('data-index', `Firma ${newIndex}`);
        }
      }
    });
  }

  downloadDocumentPdf(): void {
    if (!this.base64) {
      console.error('No se pudo descargar el PDF');
      return;
    }
    const byteCharacters = atob(this.base64);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
  
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = this.documentName + '.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  requestSignatureLink(request: SignatureRequest) {
    if (!this.companyDocument || !this.companyDocument.companyDocumentId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se puede solicitar el link de firma en este momento'
      });
      return;
    }

    // The signature requests and their signatories created from the version 2 of the API contains the participantId, 
    const signatureParticipantLinkV2$ = this.signatureParticipantsService.getSignatureParticipantLink(request.participantId!)
    .pipe(
      map(
        (response) => {
          return {
            isSuccess: response.isEnabled,
            link: response.link,
            reason: response.reason
          };
        }
      ),
      catchError((error) => {
        return of({
          isSuccess: false,
          link: '',
          reason: error.message
        });
      })
    );

    // If the participantId is not available, use the legacy method to request the signature link
    const signatureParticipantLinkLegacy$ = this.companyDocumentService.requestSignatureLink(request.rut)
    .pipe(
      map(
        (response) => {
          return {
            isSuccess: true,
            link: `https://firma.inge.cl/?k=${response.key}`,
            reason: ''
          };
        }
      ),
      catchError((error) => {
        return of({
          isSuccess: false,
          link: '',
          reason: error.message || 'No se pudo solicitar el link de firma. Por favor, intente nuevamente.'
        });
      })
    );

    // Depending of, whether the participantId is available, choose the appropriate observable to the API to request the signature link
    const execution$ = request.participantId ? signatureParticipantLinkV2$ : signatureParticipantLinkLegacy$;
    execution$.subscribe({
      next: (result) => {
        if (result.isSuccess) {
          Swal.fire({
            icon: 'success',
            title: 'Link de firma solicitado',
            text: result.link,
            confirmButtonText: 'Aceptar'
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: result.reason || 'No se pudo solicitar el link de firma. Por favor, intente nuevamente.'
          });
        }
      },
      error: (error) => {
        console.error('Error al solicitar el link de firma:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo solicitar el link de firma. Por favor, intente nuevamente.'
        });
      }
    });
  }
}

const newCompanyDocumentDetail: CompanyDocumentDetailDTO = {
  companyDocumentId: 0n,  
  description: '',         
  uri: '',                 
  createdAt: new Date(0),  
  documentType: '',        
  mymeType: '',            
  uploadedByUser: '',      
  isSignRequired: false,   
  isSigned: false,         
  signers: []              
};
